import { idbDelete, idbGet, idbGetAll, idbPut, STORES } from './idb';
import { readRefCache } from './refCache';
import { reserveNextCode } from './inspectionCode';

// الفحص المُدخل دون إنترنت يُحفظ على الجهاز بسجلّين:
// - state: حالته الحالية، تُبنى بنفس قواعد الخادم، ومنها تقرأ الشاشات.
// - ops: العمليات التي لم تُرفع بعد بترتيب حدوثها، ومنها تعيد المزامنة بناء الفحص في الخادم.
export const LOCAL_ID_PREFIX = 'L';
const DIRECT_DONE_FIELDS = new Set(['steering_done', 'engine_done', 'computer_done', 'body_done']);
const BODY_SEVERITY_ORDER = ['good', 'scratch', 'damage', 'sprayed', 'riveted', 'replaced', 'defect'];
const BODY_FLAG_FIELDS = ['is_sprayed', 'is_modified', 'is_replaced', 'is_riveted', 'is_dented', 'is_plastic', 'is_protected', 'is_loose'];

const listeners = new Set();
const draftLocks = new Map();
const objectUrls = new Map();

export function isLocalInspectionId(value) {
  return new RegExp(`^${LOCAL_ID_PREFIX}[0-9]+$`).test(String(value || ''));
}

export function subscribeDrafts(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emitChange() {
  listeners.forEach((listener) => listener());
}

export function newClientUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // randomUUID غير متاح على http عبر IP الشبكة المحلية.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').replace(/^(.{8})(.{4})(.{4})(.{4})/, '$1-$2-$3-$4-');
}

// كل تعديل على فحص يمر بالترتيب: رفع عدة صور معًا كان سيقرأ النسخة نفسها ويضيّع بعضها.
function withDraftLock(id, task) {
  const previous = draftLocks.get(id) || Promise.resolve();
  const next = previous.catch(() => {}).then(task);
  draftLocks.set(id, next.catch(() => {}));
  return next;
}

function nowIso() {
  return new Date().toISOString();
}

function sanitize(value) {
  const text = value === undefined || value === null ? '' : String(value).trim();
  return text || null;
}

function toBoolean(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

// ===== قواعد الخادم كما هي (controllers/inspection.controller.js) =====

function normalizeBodyStatuses(item) {
  const raw = item?.condition_statuses !== undefined && item?.condition_statuses !== null
    ? item.condition_statuses
    : [item?.condition_status];

  const list = (Array.isArray(raw) ? raw : String(raw || '').split(','))
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .filter((value) => value !== 'unknown');

  const unique = [...new Set(list)].sort((left, right) => {
    const leftIndex = BODY_SEVERITY_ORDER.indexOf(left);
    const rightIndex = BODY_SEVERITY_ORDER.indexOf(right);
    return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
  });

  return unique.length > 1 ? unique.filter((status) => status !== 'good') : unique;
}

function bodyRow(itemId, statuses, source, notes) {
  return {
    inspection_item_id: Number(itemId),
    condition_status: statuses.length ? statuses[statuses.length - 1] : null,
    condition_statuses: statuses,
    is_sprayed: statuses.includes('sprayed') || toBoolean(source?.is_sprayed),
    is_modified: statuses.includes('damage') || toBoolean(source?.is_modified),
    is_replaced: statuses.includes('replaced') || toBoolean(source?.is_replaced),
    is_riveted: statuses.includes('riveted') || toBoolean(source?.is_riveted),
    is_dented: statuses.includes('defect') || toBoolean(source?.is_dented),
    is_plastic: toBoolean(source?.is_plastic),
    is_protected: toBoolean(source?.is_protected),
    is_loose: toBoolean(source?.is_loose),
    notes,
    updated_at: nowIso(),
  };
}

function sectionFromEndpoint(endpoint) {
  const path = String(endpoint || '');
  return path.startsWith('sections/') ? path.slice('sections/'.length) : path;
}

function applySection(state, endpoint, items, itemsIndex) {
  const section = sectionFromEndpoint(endpoint);
  // مثل الخادم: الحذف بقسم البند المسجّل في بنود الفحص، لا بقسم المسار.
  Object.keys(state.results).forEach((itemId) => {
    const itemSection = itemsIndex.get(Number(itemId))?.section || state.results[itemId].section;
    if (itemSection === section) delete state.results[itemId];
  });

  (items || []).forEach((item) => {
    const notes = sanitize(item?.notes);
    if (!item?.item_id || (!item?.condition_type && !notes)) return;
    state.results[item.item_id] = {
      inspection_item_id: Number(item.item_id),
      condition_type: sanitize(item.condition_type),
      notes,
      section: itemsIndex.get(Number(item.item_id))?.section || section,
      updated_at: nowIso(),
    };
  });

  const doneField = `${section}_done`;
  if (DIRECT_DONE_FIELDS.has(doneField)) state.done[doneField] = true;
}

function applyBody(state, items, markDone) {
  const attachedItemIds = new Set(state.attachments.map((attachment) => String(attachment.item_id)).filter(Boolean));
  state.body = {};

  (items || []).forEach((item) => {
    if (!item?.item_id) return;
    const statuses = normalizeBodyStatuses(item);
    const notes = sanitize(item.notes);
    const hasAttachment = attachedItemIds.has(String(item.item_id)) || Boolean(item?.attachment?.id);
    if (!statuses.length && !notes && !hasAttachment) return;
    state.body[item.item_id] = bodyRow(item.item_id, statuses, item, notes);
  });

  if (markDone !== false) state.done.body_done = true;
}

function applyComputer(state, payload, markDone) {
  state.computer = {
    scan_result: sanitize(payload?.scan_result),
    field_inspection: sanitize(payload?.field_inspection),
    notes: sanitize(payload?.notes),
    updated_at: nowIso(),
  };
  if (markDone) state.done.computer_done = true;
}

// أثر رفع الصورة على نتيجة البند، كما يفعل saveAttachment في الخادم.
function applyAttachmentSideEffects(state, fields, itemsIndex) {
  const section = String(fields.section || '').toLowerCase();
  const itemId = fields.item_id ? Number(fields.item_id) : null;
  const notes = sanitize(fields.notes);

  if (section === 'body' && itemId) {
    const hasBodyPayload = notes
      || sanitize(fields.condition_status)
      || BODY_FLAG_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(fields, field));
    if (!hasBodyPayload) return;

    const existing = state.body[itemId];
    const sent = fields.condition_statuses;
    const hasSent = sent !== undefined && sent !== null && sent !== '';
    const statuses = normalizeBodyStatuses({
      condition_statuses: hasSent ? sent : (existing?.condition_statuses?.length ? existing.condition_statuses : undefined),
      condition_status: sanitize(fields.condition_status) || existing?.condition_status || null,
    });
    state.body[itemId] = bodyRow(itemId, statuses, fields, notes);
    return;
  }

  if (section === 'computer') {
    if (notes || sanitize(fields.scan_result) || sanitize(fields.field_inspection)) {
      applyComputer(state, fields, false);
    }
    return;
  }

  if (itemId) {
    const conditionType = sanitize(fields.condition_type);
    if (conditionType || notes) {
      state.results[itemId] = {
        inspection_item_id: itemId,
        condition_type: conditionType,
        notes,
        section: itemsIndex.get(itemId)?.section || fields.section || null,
        updated_at: nowIso(),
      };
    }
  }
}

// ===== فهرس بنود الفحص من البيانات المرجعية المحفوظة =====

async function loadItemsIndex() {
  const types = (await readRefCache('inspection-types')) || [];
  const keys = new Set(['body', 'steering', 'engine', 'computer']);
  types.forEach((type) => {
    if (type?.key) keys.add(type.key);
  });

  const index = new Map();
  for (const key of keys) {
    const items = (await readRefCache(`inspection-items/by-section/${key}`)) || [];
    items.forEach((item) => index.set(Number(item.id), item));
  }
  return index;
}

// ===== التخزين =====

async function readDraft(id) {
  const draft = await idbGet(STORES.drafts, id);
  if (!draft) {
    const error = new Error('الفحص غير موجود على هذا الجهاز.');
    error.status = 404;
    throw error;
  }
  return draft;
}

async function saveDraft(draft) {
  draft.updated_at = nowIso();
  await idbPut(STORES.drafts, draft);
  emitChange();
  return draft;
}

// عمليات الاستبدال الكامل (قسم، بودي، كمبيوتر، بيانات المركبة) تكفي آخر نسخة منها؛
// النسخ الأقدم يلغيها الاستبدال اللاحق في الخادم على أي حال.
function pushOp(draft, op) {
  const replaceKey = op.replaceKey || null;
  if (replaceKey) {
    draft.ops = draft.ops.filter((existing) => existing.replaceKey !== replaceKey);
  }
  draft.ops.push({ ...op, id: newClientUuid(), at: nowIso() });
  // الإنهاء يبقى آخر عملية دائمًا.
  const complete = draft.ops.find((existing) => existing.type === 'complete');
  if (complete && op.type !== 'complete') {
    draft.ops = [...draft.ops.filter((existing) => existing.type !== 'complete'), complete];
  }
}

export async function listDrafts() {
  try {
    const drafts = await idbGetAll(STORES.drafts);
    return drafts.sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)));
  } catch {
    return [];
  }
}

export async function getDraft(id) {
  return idbGet(STORES.drafts, id);
}

export async function createDraft({ form, user, auction, clientUuid = null }) {
  const auctionId = Number(auction?.id || user?.selected_auction_id || 0);
  const userId = Number(user?.id || 0);
  if (!auctionId || !userId) {
    throw new Error('تعذّر تحديد المزاد أو المستخدم. سجّل الدخول مرة واحدة مع الاتصال ثم أعد المحاولة.');
  }

  const existing = await listDrafts();
  const code = await reserveNextCode(auctionId, userId, existing.map((draft) => draft.code));
  const createdAt = nowIso();

  const draft = {
    id: `${LOCAL_ID_PREFIX}${code}`,
    code,
    client_uuid: clientUuid || newClientUuid(),
    user_id: userId,
    auction_id: auctionId,
    inspector: { id: userId, name: user?.name || '', username: user?.username || '' },
    auction: auction ? { id: auctionId, name: auction.name || '', phone: auction.phone || null, address: auction.address || null } : { id: auctionId },
    created_at: createdAt,
    updated_at: createdAt,
    state: {
      status: 'in_progress',
      form: { ...form },
      done: { steering_done: false, engine_done: false, computer_done: false, body_done: false },
      results: {},
      body: {},
      computer: null,
      attachments: [],
    },
    ops: [],
    sync: { server_id: null, server_code: null, last_error: null, last_attempt_at: null, code_changed: false },
  };

  await idbPut(STORES.drafts, draft);
  emitChange();
  return draft;
}

export function updateDraftForm(id, form) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    draft.state.form = { ...draft.state.form, ...form };
    // قبل الإنشاء في الخادم يكفي تحديث البيانات؛ الإنشاء يرسل آخر نسخة.
    if (draft.sync.server_id) pushOp(draft, { type: 'update', replaceKey: 'update' });
    return saveDraft(draft);
  });
}

export function saveDraftSection(id, endpoint, items) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    applySection(draft.state, endpoint, items, await loadItemsIndex());
    pushOp(draft, { type: 'section', endpoint, items: stripAttachments(items), replaceKey: `section:${endpoint}` });
    return saveDraft(draft);
  });
}

export function saveDraftBody(id, items, markDone) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    applyBody(draft.state, items, markDone);
    // mark_done يُحفظ كما أُرسل آخر مرة، لكن إن وصل البودي إلى «مكتمل» مرة لا يتراجع.
    const previous = draft.ops.find((op) => op.replaceKey === 'body');
    const effectiveMarkDone = markDone !== false || Boolean(previous?.mark_done);
    pushOp(draft, { type: 'body', items: stripAttachments(items), mark_done: effectiveMarkDone, replaceKey: 'body' });
    return saveDraft(draft);
  });
}

export function saveDraftComputer(id, payload) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    applyComputer(draft.state, payload, true);
    pushOp(draft, { type: 'computer', payload: { ...payload }, replaceKey: 'computer' });
    return saveDraft(draft);
  });
}

export function completeDraft(id) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    draft.state.status = 'completed';
    draft.state.completed_at = nowIso();
    pushOp(draft, { type: 'complete', replaceKey: 'complete' });
    return saveDraft(draft);
  });
}

// الصورة تُرفع مع حقول البند لحظة التقاطها؛ نحفظ الحقول كما هي لنعيد إرسالها عند المزامنة.
export function addDraftAttachment(id, file, fields) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    const attachmentId = `a-${newClientUuid()}`;
    const blobKey = `${draft.id}:${attachmentId}`;
    const section = String(fields.section || '').toLowerCase();
    const allowMultiple = toBoolean(fields.allow_multiple) || section === 'computer';
    const itemId = fields.item_id ? Number(fields.item_id) : null;

    if (!allowMultiple) {
      const replaced = draft.state.attachments.filter((attachment) => (
        String(attachment.section || '') === String(fields.section || '')
        && String(attachment.item_id || '') === String(itemId || '')
      ));
      for (const attachment of replaced) {
        // eslint-disable-next-line no-await-in-loop
        await discardAttachment(draft, attachment);
      }
    }

    await idbPut(STORES.blobs, file, blobKey);
    const attachment = {
      id: attachmentId,
      blob_key: blobKey,
      section: fields.section || null,
      item_id: itemId,
      file_name: file?.name || 'image.jpg',
      size: file?.size || null,
      created_at: nowIso(),
      server_id: null,
    };
    draft.state.attachments.unshift(attachment);
    applyAttachmentSideEffects(draft.state, fields, await loadItemsIndex());
    pushOp(draft, { type: 'attachment', attachment_id: attachmentId, fields: { ...fields } });
    await saveDraft(draft);
    return attachmentView(draft, attachment);
  });
}

// صورة لم تُرفع تُحذف من الجهاز وتُلغى عملية رفعها؛ صورة رُفعت يُحذف أثرها من الخادم عند المزامنة.
async function discardAttachment(draft, attachment) {
  draft.state.attachments = draft.state.attachments.filter((entry) => entry.id !== attachment.id);
  const pendingIndex = draft.ops.findIndex((op) => op.type === 'attachment' && op.attachment_id === attachment.id);
  if (pendingIndex !== -1) draft.ops.splice(pendingIndex, 1);
  if (attachment.server_id) pushOp(draft, { type: 'delete-attachment', server_id: attachment.server_id });
  revokeObjectUrl(attachment.blob_key);
  await idbDelete(STORES.blobs, attachment.blob_key);
}

export function deleteDraftAttachment(id, attachmentId) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    const attachment = draft.state.attachments.find((entry) => entry.id === attachmentId);
    if (!attachment) {
      const error = new Error('الصورة غير موجودة.');
      error.status = 404;
      throw error;
    }
    await discardAttachment(draft, attachment);
    return saveDraft(draft);
  });
}

export function deleteDraft(id) {
  return withDraftLock(id, async () => {
    const draft = await idbGet(STORES.drafts, id);
    if (!draft) return;
    for (const attachment of draft.state.attachments) {
      revokeObjectUrl(attachment.blob_key);
      // eslint-disable-next-line no-await-in-loop
      await idbDelete(STORES.blobs, attachment.blob_key);
    }
    await idbDelete(STORES.drafts, id);
    emitChange();
  });
}

// تعديل داخلي تستخدمه المزامنة لتسجيل ما رُفع.
export function mutateDraft(id, mutator) {
  return withDraftLock(id, async () => {
    const draft = await readDraft(id);
    await mutator(draft);
    return saveDraft(draft);
  });
}

export function readAttachmentBlob(blobKey) {
  return idbGet(STORES.blobs, blobKey);
}

function stripAttachments(items) {
  return (items || []).map((item) => {
    if (!item || typeof item !== 'object') return item;
    const { attachment, ...rest } = item;
    // الخادم يقرأ attachment.id ليُبقي سطر البودي الذي له صورة، والرقم المحلي يكفي لذلك.
    return attachment?.id ? { ...rest, attachment: { id: attachment.id } } : rest;
  });
}

// ===== بناء الفحص بنفس شكل رد الخادم (fetchInspectionById) =====

function revokeObjectUrl(blobKey) {
  const url = objectUrls.get(blobKey);
  if (url) {
    URL.revokeObjectURL(url);
    objectUrls.delete(blobKey);
  }
}

async function objectUrlFor(blobKey) {
  if (objectUrls.has(blobKey)) return objectUrls.get(blobKey);
  const blob = await readAttachmentBlob(blobKey);
  if (!blob) return '';
  const url = URL.createObjectURL(blob);
  objectUrls.set(blobKey, url);
  return url;
}

async function attachmentView(draft, attachment, itemsIndex = null) {
  const url = await objectUrlFor(attachment.blob_key);
  const item = attachment.item_id && itemsIndex ? itemsIndex.get(Number(attachment.item_id)) : null;
  return {
    id: attachment.id,
    inspection_id: draft.id,
    inspection_item_id: attachment.item_id,
    section: attachment.section,
    file_path: url,
    original_image_path: url,
    optimized_image_path: url,
    thumbnail_path: url,
    image_size_before: attachment.size,
    image_size_after: attachment.size,
    optimization_status: 'local',
    optimization_error: null,
    created_at: attachment.created_at,
    updated_at: attachment.created_at,
    inspection_item: item ? { id: item.id, name: item.name, section: item.section, diagram_zone_key: item.diagram_zone_key } : null,
    is_local: true,
  };
}

function inferBodyStyle(value) {
  const shape = String(value || '').toLowerCase();
  if (!shape) return null;
  if (shape.includes('trailer') || shape.includes('تريلا') || shape.includes('قاطرة')) return 'trailer';
  if (shape.includes('box_truck') || shape.includes('صندوق') || shape.includes('مقفص')) return 'box_truck';
  if (shape.includes('chassis') || shape.includes('شاسي') || shape.includes('شاصي')) return 'chassis_cab';
  if (shape.includes('suv')) return 'suv';
  if (shape.includes('coupe') || shape.includes('كوبيه') || shape.includes('كوبي')) return 'coupe';
  if (shape.includes('pickup') || shape.includes('بيك')) return 'pickup';
  if (shape.includes('hatch') || shape.includes('هاتش')) return 'hatchback';
  if (shape.includes('van') || shape.includes('فان')) return 'van';
  if (shape.includes('sedan') || shape.includes('سيدان')) return 'sedan';
  return null;
}

function mapItem(item) {
  if (!item) return null;
  const inspectionType = item.inspection_type || null;
  return { ...item, inspection_type: inspectionType, inspectionType };
}

async function findById(path, id) {
  if (!id) return null;
  const rows = (await readRefCache(path)) || [];
  return rows.find((row) => String(row.id) === String(id)) || null;
}

export async function buildInspectionView(id) {
  const draft = await readDraft(id);
  const { state } = draft;
  const form = state.form || {};
  const itemsIndex = await loadItemsIndex();

  const [manufacturer, category, model] = await Promise.all([
    findById('manufacturers', form.manufacturer_id),
    findById('categories', form.category_id),
    findById('models', form.model_id),
  ]);

  const vehicleModel = model ? { id: model.id, name: model.name, body_style: model.body_style || null } : null;
  const vehicle = {
    owner_id: null,
    auction_id: null,
    manufacturer_id: form.manufacturer_id ? Number(form.manufacturer_id) : null,
    category_id: form.category_id ? Number(form.category_id) : null,
    model_id: form.model_id ? Number(form.model_id) : null,
    vin: sanitize(form.vin),
    plate_number: sanitize(form.plate_number),
    plate_code: sanitize(form.plate_code),
    engine_size: sanitize(form.engine_size),
    warranty_status: sanitize(form.warranty_status),
    odometer: sanitize(form.odometer),
    transmission: sanitize(form.transmission),
    keys_count: sanitize(form.keys_count),
    fuel_type: sanitize(form.fuel_type),
    cylinders: sanitize(form.cylinders),
    engine_condition: sanitize(form.engine_condition),
    drive_type: sanitize(form.drive_type),
    gear_type: sanitize(form.gear_type),
    body_shape: sanitize(form.body_shape),
    seat_type: sanitize(form.seat_type),
    seats_count: sanitize(form.seats_count),
    exterior_color: sanitize(form.exterior_color),
    interior_color: sanitize(form.interior_color),
    owner: { id: null, name: sanitize(form.owner_name) || 'غير محدد', phone: sanitize(form.owner_phone) },
    manufacturer: manufacturer ? { id: manufacturer.id, name: manufacturer.name, logo_path: manufacturer.logo_path || null } : null,
    category: category ? { id: category.id, name: category.name } : null,
    vehicle_model: vehicleModel,
  };
  vehicle.vehicleModel = vehicleModel;

  const attachments = [];
  for (const attachment of state.attachments) {
    // eslint-disable-next-line no-await-in-loop
    attachments.push(await attachmentView(draft, attachment, itemsIndex));
  }

  const code = draft.sync.server_code || draft.code;
  const inspectionDate = form.inspection_date || String(draft.created_at).slice(0, 10);

  return {
    id: draft.id,
    is_local: true,
    vehicle_id: null,
    inspector_id: draft.user_id,
    branch_id: draft.auction_id,
    branch_serial: null,
    inspection_code: code,
    display_number: code,
    status: state.status,
    ...state.done,
    completed_at: state.completed_at || null,
    created_at: draft.created_at,
    updated_at: draft.updated_at,
    vehicle,
    auction: draft.auction,
    branch: draft.auction,
    inspector: draft.inspector,
    inspection_results: Object.values(state.results).map((row) => ({
      id: `r-${row.inspection_item_id}`,
      inspection_id: draft.id,
      inspection_item_id: row.inspection_item_id,
      condition_type: row.condition_type,
      notes: row.notes,
      created_at: row.updated_at,
      updated_at: row.updated_at,
      inspection_item: mapItem(itemsIndex.get(Number(row.inspection_item_id)))
        || { id: row.inspection_item_id, section: row.section },
    })),
    body_results: Object.values(state.body).map((row) => ({
      id: `b-${row.inspection_item_id}`,
      inspection_id: draft.id,
      ...row,
      created_at: row.updated_at,
      inspection_item: mapItem(itemsIndex.get(Number(row.inspection_item_id))),
    })),
    computer_result: state.computer ? { id: `c-${draft.id}`, inspection_id: draft.id, ...state.computer } : null,
    inspection_score: null,
    attachments,
    diagram_body_style: inferBodyStyle(form.body_shape) || inferBodyStyle(vehicleModel?.body_style) || 'sedan',
    formatted_created_at: draft.created_at,
    inspection_date: inspectionDate,
    notes: '',
    pending_ops: draft.ops.length,
  };
}

export function draftSummary(draft) {
  const form = draft.state?.form || {};
  return {
    id: draft.id,
    code: draft.sync?.server_code || draft.code,
    status: draft.state?.status,
    created_at: draft.created_at,
    updated_at: draft.updated_at,
    vin: form.vin || '',
    plate: [form.plate_number, form.plate_code].filter(Boolean).join(' '),
    owner_name: form.owner_name || '',
    manufacturer_id: form.manufacturer_id || null,
    model_id: form.model_id || null,
    auction_name: draft.auction?.name || '',
    pending_photos: draft.ops.filter((op) => op.type === 'attachment').length,
    pending_ops: draft.ops.length + (draft.sync?.server_id ? 0 : 1),
    server_id: draft.sync?.server_id || null,
    last_error: draft.sync?.last_error || null,
    last_attempt_at: draft.sync?.last_attempt_at || null,
    code_changed: Boolean(draft.sync?.code_changed),
    user_id: draft.user_id,
    auction_id: draft.auction_id,
  };
}
